/* eslint-disable import/no-extraneous-dependencies */
import { Country } from 'country-state-city';
import { IUserData } from '../user/user-data.interface';
import {
  LocalQcLocationSim,
  readLocalQcLocationSim,
} from '../../../environments';

/** Same prefix as Settings → Profile location cache. */
export const LOCATION_CACHE_PREFIX = 'qc.user.location.v1:';

export interface ResolvedProfileLocation {
  countryCode?: string;
  countryName?: string;
  stateName?: string;
  cityName?: string;
}

type CachedLocation = {
  city?: string;
  state?: string;
  country?: string;
  code?: string;
  stateId?: string;
};

export const ALLOWED_LOCATION_FIXTURE: ResolvedProfileLocation = {
  countryCode: 'US',
  countryName: 'United States of America',
  stateName: 'Texas',
  cityName: 'Austin',
};

export const RESTRICTED_LOCATION_FIXTURE: ResolvedProfileLocation = {
  countryCode: 'US',
  countryName: 'United States of America',
  stateName: 'California',
  cityName: 'Los Angeles',
};

function isSentinelCountryCode(code: string | undefined): boolean {
  return !code || code === 'XX';
}

function isSentinelCity(city: string | undefined): boolean {
  return !city || city === 'Unknown';
}

export function countryDisplayName(countryCode?: string): string | undefined {
  if (!countryCode || isSentinelCountryCode(countryCode)) {
    return undefined;
  }
  try {
    const match = Country.getCountryByCode(countryCode);
    if (match?.name === 'United States') {
      return 'United States of America';
    }
    return match?.name || countryCode;
  } catch {
    return countryCode;
  }
}

function withCountryName(
  location: ResolvedProfileLocation,
): ResolvedProfileLocation {
  return {
    ...location,
    countryName:
      location.countryName || countryDisplayName(location.countryCode),
  };
}

function readLocationCache(userId: string): CachedLocation | null {
  if (typeof localStorage === 'undefined' || !userId) {
    return null;
  }
  try {
    const raw = localStorage.getItem(`${LOCATION_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedLocation;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Country/state/city from onboarding or Settings, never IP geo.
 * Order: user sync fields → userStateFlags[0] → local cache.
 */
export function resolveProfileLocation(
  user?: Partial<IUserData> | null,
): ResolvedProfileLocation {
  const rawCountry = (user?.countryCode || '').trim();
  if (!isSentinelCountryCode(rawCountry)) {
    const city = (user?.cityName || '').trim();
    return withCountryName({
      countryCode: rawCountry,
      stateName: (user?.stateName || '').trim() || undefined,
      cityName: isSentinelCity(city) ? undefined : city,
    });
  }

  const flag = user?.userStateFlags?.[0];
  const flagCountry = (flag?.countryCode || '').trim();
  if (!isSentinelCountryCode(flagCountry)) {
    return withCountryName({
      countryCode: flagCountry,
      stateName: (flag?.state || '').trim() || undefined,
    });
  }

  const userId = String(user?.id || user?._id || '');
  const cached = readLocationCache(userId);
  const cachedCountry = (cached?.code || '').trim();
  if (!isSentinelCountryCode(cachedCountry)) {
    const city = (cached?.city || '').trim();
    return withCountryName({
      countryCode: cachedCountry,
      stateName: (cached?.state || '').trim() || undefined,
      cityName: isSentinelCity(city) ? undefined : city,
    });
  }

  return {};
}

/** Localhost ?qcRegion / ?qcCountry overrides. No-op on deployed hosts. */
export function applyLocalLocationSim(
  profile: ResolvedProfileLocation,
  href = typeof window !== 'undefined' ? window.location.href : '',
  sim: LocalQcLocationSim | null = readLocalQcLocationSim(href),
): ResolvedProfileLocation {
  if (!sim) {
    return profile;
  }

  const hasExplicit =
    Boolean(sim.countryCode) || Boolean(sim.stateName) || Boolean(sim.cityName);
  let fixture: ResolvedProfileLocation | undefined;
  if (sim.region === 'restricted') {
    fixture = RESTRICTED_LOCATION_FIXTURE;
  } else if (sim.region === 'allowed') {
    fixture = ALLOWED_LOCATION_FIXTURE;
  }

  if (hasExplicit) {
    return withCountryName({
      countryCode: sim.countryCode || profile.countryCode,
      stateName: sim.stateName || profile.stateName,
      cityName: sim.cityName || profile.cityName,
    });
  }

  return fixture || profile;
}
